import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Area, ImageType, JobIssue, Media, PostCompleteStatus, SyncableStatus } from "../../../../../core/database/db";
import { Bq9DataService } from "../services/bq9-data.service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";
import { UNASSIGNED_AREA } from "../../../../../domains/areas/models/AreaDTO";
import { DomSanitizer } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";
import { BlobService } from "../../../../../shared/services/blob.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../domains/media/utils/media-order.util";
import { CONSTANTS } from "../../../../../constants";
import { ImagesModalComponent } from "../../../../../domains/media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../../../domains/media/components/images-modal/bqa-image-item";
import { ASSIGN_TO_ME_OPTION_LABEL, SELF_ASSIGNED_VENDOR, VendorDTO, withAssignToMeOption } from "../../../../../domains/vendors/models/VendorDTO";
import { v4 as uuidv4 } from "uuid";
import { FormFieldComponent } from "../../../../../shared/components/form-field/form-field.component";
import { SelectFieldComponent } from "../../../../../shared/components/select-field/select-field.component";
import { TextareaComponent } from "../../../../../shared/components/textarea/textarea.component";
import { PhotoCaptureComponent } from "../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../shared/components/section-header/section-header.component";
import { StandardBottomSheetComponent } from "../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { ModalIcon, UiModalService } from "../../../../../shared/components/modal/ui-modal.service";

@Component({
  selector: "app-bq9-not-complete",
  imports: [ReactiveFormsModule, FormFieldComponent, SelectFieldComponent, TextareaComponent, PhotoCaptureComponent, SectionHeaderComponent, StandardBottomSheetComponent, VerticalButtonGroupComponent],
  templateUrl: "./bq9-not-complete.component.html",
  styleUrl: "./bq9-not-complete.component.scss"
})
export class Bq9NotCompleteComponent implements OnInit, OnDestroy, AfterViewInit {
  isSheetOpen = signal(false);
  isAddNewItemMode = false;
  isDeleting = false;
  area: Area = {} as Area;
  issue: JobIssue = {} as JobIssue;
  areas: Area[] = [];

  images = signal<Media[]>([]);
  allImagesRaw = computed(() => this.images().filter(im => !im.deleted));
  allImages = computed(() =>
    this.allImagesRaw()
      .filter(img => img.base64Image || img.resourceUri)
      .map(img => {
        const azureBlobUri = img.base64Image
          ? this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image))
          : img.resourceUri!;
        return this.sanitizer.bypassSecurityTrustUrl(azureBlobUri);
      })
  );
  galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(image => (image.base64Image || image.resourceUri) && !image.deleted)
      .map(image => {
        let azureBlobUri = "";
        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(image.base64Image));
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }
        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      })
  );

  maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;
  private objectUrls = new Set<string>();

  readonly issueTypeOptions = [
    { label: "Follow-Up", value: PostCompleteStatus.FollowUp },
    { label: "Seasonal", value: PostCompleteStatus.Seasonal }
  ];

  form = new FormGroup({
    lotAreaIdentifier: new FormControl<string | null>(null, Validators.required),
    text: new FormControl("", Validators.required),
    postCompleteStatus: new FormControl<PostCompleteStatus>(PostCompleteStatus.FollowUp, { nonNullable: true, validators: Validators.required }),
    externalVendorId: new FormControl<number | null>(null),
    photos: new FormControl<Media[]>([])
  });

  vendors: VendorDTO[] = [];
  protected readonly selfAssignedVendorId = SELF_ASSIGNED_VENDOR.id;
  protected readonly assignToMeLabel = ASSIGN_TO_ME_OPTION_LABEL;
  formSubmitted = false;
  private issueIdentifierForEncoding!: string;
  private initialFormSnapshot = "";
  private initialImageFileIdentifiers = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private bq9DataService: Bq9DataService,
    private toastService: UiToastService,
    private router: ExtendedRouter,
    private sanitizer: DomSanitizer,
    private blobService: BlobService,
    public imageCompressionPipelineService: ImageCompressionPipelineService,
    private dialog: Dialog = inject(Dialog),
    private modalService: UiModalService = inject(UiModalService)
  ) { }

  async ngOnInit(): Promise<void> {
    this.isAddNewItemMode = !!this.route.snapshot.data["isAddNewItem"];
    this.issueIdentifierForEncoding = uuidv4();

    const areas = [...(this.bq9DataService.areas() ?? []), UNASSIGNED_AREA];
    this.areas = areas;
    this.vendors = withAssignToMeOption(this.bq9DataService.vendors());

    if (this.isAddNewItemMode) {
      this.form.get("photos")!.setValidators(control => (control.value?.length ? null : { required: true }));
      this.form.get("photos")!.updateValueAndValidity();
      return;
    }

    const areaId = parseInt(
      this.route.snapshot.paramMap.get("areaId")
      ?? this.route.parent?.snapshot.paramMap.get("areaId")
      ?? "0"
    );
    const issueId = parseInt(
      this.route.snapshot.paramMap.get("issueId")
      ?? this.route.parent?.snapshot.paramMap.get("issueId")
      ?? "0"
    );
    const issue = this.bq9DataService.jobIssues()?.find(i => i.id === issueId);
    const area = areas.find(a => a.id === areaId);

    if (!area || !issue) {
      this.toastService.error("Error loading issue data. Please try again.");
      this.goBack();
      return;
    }

    this.area = area;
    this.issue = issue;
    this.form.patchValue({
      lotAreaIdentifier: issue.lotAreaIdentifier ?? null,
      text: issue.text,
      postCompleteStatus: issue.postCompleteStatus ?? PostCompleteStatus.FollowUp
    });

    const issueMedia = (this.bq9DataService.media() ?? [])
      .filter(m => m.parentIdentifier === issue.identifier && !m.deleted && m.imageType === ImageType.Issue)
      .map(img => ({
        ...img,
        id: undefined,
        fileIdentifier: img.fileIdentifier,
        parentIdentifier: this.issueIdentifierForEncoding,
        syncStatus: SyncableStatus.Pending,
        createdAt: new Date(),
        updatedAt: new Date()
      }));
    this.images.set(issueMedia);
    this.initialImageFileIdentifiers = new Set(
      issueMedia.map(m => m.fileIdentifier).filter((id): id is string => !!id)
    );

    const existingAssignment = this.bq9DataService.getVendorAssignmentForIssue(issue);
    const preselectedVendorId = existingAssignment
      ? (this.vendors.find(v => v.id === existingAssignment.externalVendorId)?.id ?? null)
      : null;

    this.form.patchValue({ externalVendorId: preselectedVendorId });
    this.initialFormSnapshot = JSON.stringify(this.form.value);
  }

  ngAfterViewInit(): void {
    // Render the sheet closed on the first frame, then open it on the next so it slides up on entry.
    requestAnimationFrame(() => requestAnimationFrame(() => this.isSheetOpen.set(true)));
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }

  async onSave(): Promise<void> {
    this.formSubmitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    await this.imageCompressionPipelineService.waitForCompletion();
    const mediaToSave: Media[] = this.images().map(image => {
      const { file: _file, ...media } = image as MediaForCompression;
      return media;
    });

    const { text, postCompleteStatus, externalVendorId, lotAreaIdentifier } = this.form.value;
    const selectedVendor = externalVendorId ? (this.vendors.find(v => v.id === externalVendorId) ?? null) : null;
    const savedLotAreaIdentifier = lotAreaIdentifier === UNASSIGNED_AREA.identifier ? undefined : (lotAreaIdentifier ?? undefined);

    const isEditingExistingBq9 = !this.isAddNewItemMode && !!this.issue.postCompleteStatus;

    if (isEditingExistingBq9) {
      await this.bq9DataService.updateNotCompleteIssue(
        this.issue,
        { text: text!, postCompleteStatus: postCompleteStatus!, lotAreaIdentifier: savedLotAreaIdentifier },
        mediaToSave,
        selectedVendor
      );
    } else {
      const bq9Issue: JobIssue = {
        identifier: this.issueIdentifierForEncoding,
        parentIdentifier: this.isAddNewItemMode ? undefined : this.issue.identifier,
        text: text!,
        postCompleteStatus: postCompleteStatus!,
        lotAreaIdentifier: savedLotAreaIdentifier,
        isSelfAssigned: false,
        isComplete: false,
        isConfirmedByHomeowner: false,
        jobId: this.bq9DataService.job()!.id!,
        syncStatus: SyncableStatus.Pending,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await this.bq9DataService.saveNotCompleteIssue(bq9Issue, mediaToSave, selectedVendor);
    }

    this.toastService.success(this.isAddNewItemMode ? "New item added successfully." : "Item documented successfully.");
    this.goToCelebration();
  }

  get footerButtons(): VerticalButton[] {
    const compressing = this.imageCompressionPipelineService.isCompressingImages();
    if (this.isAddNewItemMode) {
      return [
        {
          label: "Add New Item",
          severity: "contrast",
          disabled: compressing,
          styleClass: "bqa-primary-btn",
          action: () => this.onSave(),
          testId: "bq9-not-complete-btn-save"
        },
        {
          label: "Cancel",
          severity: "secondary",
          text: true,
          styleClass: "bqa-secondary-btn",
          action: () => this.closeSheetAndNavigate(() => this.goBack()),
          testId: "bq9-not-complete-btn-cancel"
        }

      ];
    }
    return [
      {
        label: "Save & Return to List",
        severity: "contrast",
        disabled: compressing,
        styleClass: "bqa-primary-btn",
        action: () => this.onSave(),
        testId: "bq9-not-complete-btn-save-return"
      },
      {
        label: this.issue?.parentIdentifier ? "Delete Details & Reset Item" : "Delete Item",
        severity: "danger",
        outlined: true,
        disabled: this.isDeleting,
        styleClass: "bqa-danger-btn",
        action: () => this.showResetModal(),
        testId: "bq9-not-complete-btn-reset"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.showCancelModal(),
        testId: "bq9-not-complete-btn-cancel"
      }
    ];
  }

  goBack(): void {
    this.router.navigateWithPersistedState([".."], { relativeTo: this.route });
  }

  goToCelebration(): void {
    const path = this.isAddNewItemMode ? [".."] : ["../../../../../"];
    this.router.navigateWithPersistedState(path, { relativeTo: this.route });
  }

  onOverlayDismiss(): void {
    // The overlay never auto-closes (preventOverlayClose); tapping the backdrop
    // mirrors the Cancel button so the sheet stays open until the user confirms.
    if (this.isAddNewItemMode) {
      this.closeSheetAndNavigate(() => this.goBack());
    } else {
      this.showCancelModal();
    }
  }

  private hasUnsavedChanges(): boolean {
    const currentIds = new Set(
      this.images()
        .filter(img => !img.deleted)
        .map(img => img.fileIdentifier)
        .filter((id): id is string => !!id)
    );
    const imageChanged =
      currentIds.size !== this.initialImageFileIdentifiers.size ||
      [...currentIds].some(id => !this.initialImageFileIdentifiers.has(id));
    const formChanged = JSON.stringify(this.form.value) !== this.initialFormSnapshot;
    return formChanged || imageChanged;
  }

  /**
   * True when the sheet was opened from the issue-details page (documenting a
   * not-yet-classified item). Documented items are opened straight from the
   * celebration list, so they carry a `postCompleteStatus`.
   */
  private get openedFromIssueDetails(): boolean {
    return !this.isAddNewItemMode && !this.issue.postCompleteStatus;
  }

  private readonly SHEET_CLOSE_ANIMATION_MS = 300;

  /** Slide the sheet down, then navigate once the close animation finishes. */
  private closeSheetAndNavigate(navigate: () => void): void {
    this.isSheetOpen.set(false);
    setTimeout(navigate, this.SHEET_CLOSE_ANIMATION_MS);
  }

  private dismissToPreviousPage(): void {
    this.closeSheetAndNavigate(
      this.openedFromIssueDetails ? () => this.goBack() : () => this.goToCelebration()
    );
  }

  showCancelModal(onDecline: () => void = () => { }): void {
    if (!this.hasUnsavedChanges()) {
      this.dismissToPreviousPage();
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to proceed?",
      message: "Unsaved changes will be lost.",
      cancelLabel: "No",
      onCancel: onDecline,
      actionLabel: "Yes",
      onAction: () => this.dismissToPreviousPage(),
      isDestructive: true
    });
  }

  showResetModal(): void {
    const isReset = !!this.issue.parentIdentifier;
    this.modalService.showModal({
      icon: ModalIcon.None,
      header: isReset ? "Reset Item" : "Delete Item",
      subHeader: "Are you sure you want to proceed?",
      message: isReset
        ? "This will delete all details you entered and reset this item."
        : "This will permanently delete this item.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.resetItem(),
      isDestructive: true
    });
  }

  async resetItem(): Promise<void> {
    const isReset = !!this.issue.parentIdentifier;
    this.isDeleting = true;
    try {
      if (isReset) {
        await this.bq9DataService.resetNotCompleteIssue(this.issue);
      } else {
        await this.bq9DataService.deleteAddedItem(this.issue);
      }
      this.goToCelebration();
    } catch {
      this.toastService.error(
        isReset ? "Failed to reset item. Please try again." : "Failed to delete item. Please try again."
      );
    } finally {
      this.isDeleting = false;
    }
  }

  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }
    const mediaRecords = await this.imageCompressionPipelineService.encode(
      files, this.issueIdentifierForEncoding, ImageType.Issue
    );
    this.images.update(existing => insertAt(existing, mediaRecords, insertIndex));
    this.form.get("photos")!.setValue(this.allImagesRaw(), { emitEvent: false });
    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      this.images.update(existing => existing.map(originalImage => {
        const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
        if (compressedImage) {
          const { file: _file, ...media } = compressedImage as MediaForCompression;
          return { ...originalImage, ...media } as Media;
        }
        return originalImage;
      }));
    });
    return mediaRecords;
  }

  openImagesModal(index: number): void {
    const dialogRef = this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file),
        backdropClass: "images-modal-backdrop"
      }
    });
    dialogRef.closed.subscribe(_ => { });
  }

  async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.images().findIndex(img => img.fileIdentifier === originalFileIdentifier);
    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  handleImageDeleted(fileIdentifier: string): void {
    this.images.update(existing => existing.filter(img => img.fileIdentifier !== fileIdentifier));
    this.form.get("photos")!.setValue(this.allImagesRaw(), { emitEvent: false });
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}
